<!-- Responsibility: List and select prepared player vessel profiles.
Scope: Owns vessel data fetching and selection triggers. -->
<vessels-view class="menu-section active menu-overlay">
  <h3 class="lobby-section-title">VESSEL GALLERY</h3>
  <p class="lobby-hint">Choose a compiled vessel profile, or forge a fresh one after choosing a world.</p>
  
  <div class="selection-banner { state.selectedWorld ? 'ready' : 'missing' }">
    <b>{ state.selectedWorld ? 'WORLD READY' : 'WORLD REQUIRED' }</b>
    <span>{ state.selectedWorld ? selectedWorldLabel() : 'Pick a world first so vessel creation has a reality to inhabit.' }</span>
  </div>
  
  <button class="action-btn primary" disabled={ !state.selectedWorld } onclick={ forgeVessel }>
    FORGE NEW VESSEL <span>✦</span>
  </button>
  
  <div class="save-list">
    <p if={ state.loading && !state.vessels.length } class="lobby-hint">Scanning vessels...</p>
    <p if={ state.error } class="lobby-error">{ state.error }</p>
    <p if={ !state.loading && !state.error && !state.vessels.length } class="lobby-hint">No reusable vessels found yet.</p>

    <div if={ state.loading && !state.vessels.length } class="loading-stack" aria-busy="true">
      <button each={ _ in LOADING_ROWS } type="button" class="vessel-slot loading-skeleton" disabled aria-hidden="true">
        <span class="loading-avatar"></span>
        <span class="vessel-main loading-grid">
          <span class="vessel-head">
            <span class="loading-line long"></span>
            <span class="loading-chip"></span>
          </span>
          <span class="loading-line full"></span>
          <span class="loading-line mid"></span>
        </span>
      </button>
    </div>
    
    <button
      each={ vessel in state.vessels }
      type="button"
      class="vessel-slot { state.selectedVesselKey === vessel.key ? 'active' : '' }"
      data-vessel-key={ vessel.key }
      onclick={ selectVessel }
    >
      <img if={ vessel.portraitUrl } src={ vessel.portraitUrl } alt={ vessel.identity } class="vessel-portrait">
      <span class="vessel-main">
        <span class="vessel-head">
          <b>{ vessel.identity }</b>
          <small>{ vessel.origin.replace('imported_', '').toUpperCase() }</small>
        </span>
        <span class="vessel-bio">{ vessel.bio }</span>
        <span class="vessel-meta">{ vessel.skillCount } skills · { vessel.abilityCount } abilities · { vessel.traitCount } traits</span>
      </span>
    </button>
  </div>

  <a class="nav-link" onclick={ goHub }>← BACK TO HUB</a>

  <script>
    import * as gameStore from '../../game-store.js'
    import { navigateState, sysNotify } from '../../app-states.js'
    import { playEffect } from '../../engine/audio.js'
    import { persistenceService } from '../../services/persistence-service.ts'
    import { LOBBY_SELECTION_KEY } from '../lobby/lobby-state.ts'

    const LOADING_ROWS = [0, 1, 2, 3]

    export default {
      state: {
        vessels: [],
        loading: true,
        error: null,
        selectedWorld: null,
        selectedVesselKey: null,
      },

      onMounted() {
        this.restoreSelection()
        this.loadVessels()
      },

      restoreSelection() {
        try {
          const raw = sessionStorage.getItem(LOBBY_SELECTION_KEY)
          if (!raw) return
          const parsed = JSON.parse(raw)
          this.update({
            selectedVesselKey: String(parsed?.selectedVesselKey || '').trim() || null,
            selectedWorldId: String(parsed?.selectedWorldSaveId || '').trim() || null
          })
          
          if (this.state.selectedWorldId || gameStore.importedWorldData) {
            this.update({ selectedWorld: gameStore.importedWorldData || this.state.selectedWorldId })
          }
        } catch {}
      },

      persistSelection() {
        try {
          const raw = sessionStorage.getItem(LOBBY_SELECTION_KEY)
          const parsed = raw ? JSON.parse(raw) : {}
          sessionStorage.setItem(LOBBY_SELECTION_KEY, JSON.stringify({
            ...parsed,
            selectedVesselKey: this.state.selectedVesselKey || null,
          }))
        } catch {}
      },

      async loadVessels() {
        this.update({ loading: true, error: null, vessels: [] })
        try {
          const vessels = await persistenceService.listVessels()
          const selectedVesselKey = vessels.some(v => v.key === this.state.selectedVesselKey)
            ? this.state.selectedVesselKey
            : null
          this.update({ vessels, loading: false, selectedVesselKey })
          this.persistSelection()
        } catch {
          this.update({ loading: false, error: 'Error loading vessel gallery.' })
        }
      },

      selectedWorldLabel() {
        const world = this.state.selectedWorld
        if (!world) return ''
        if (typeof world === 'string') return world
        return String(world.seedLabel || world.seed || world.roomName || '').trim() || 'Selected world'
      },

      async selectVessel(e) {
        const key = String(e?.currentTarget?.dataset?.vesselKey ?? '').trim()
        if (!key) return
        const vessel = (this.state.vessels || []).find(entry => entry.key === key)
        if (!vessel) return
        playEffect('ui_open')
        gameStore.setImportedPlayerData(vessel.playerData)
        this.update({ selectedVesselKey: key })
        this.persistSelection()
        
        if (this.state.selectedWorld) {
          sysNotify('Vessel ready. Review the pairing and launch.')
          navigateState('pairing')
        } else {
          sysNotify('Vessel ready. Choose a world next.')
          navigateState('worlds')
        }
      },

      forgeVessel() {
        if (!this.state.selectedWorld) return
        const selectedVessel = (this.state.vessels || []).find(v => v.key === this.state.selectedVesselKey)
        gameStore.setImportedPlayerData(selectedVessel?.playerData ?? null)
        navigateState('forging')
      },

      goHub() {
        navigateState('hub')
      }
    }
  </script>

  <style>
    vessels-view {
      display: flex;
      flex-direction: column;
      gap: var(--space-lg);
      width: 100%;
      max-width: min(100%, 78rem);
    }

    .lobby-section-title {
      color: var(--text-muted);
      font-size: 0.92rem;
      letter-spacing: 0.14em;
      border-bottom: 1px solid var(--border);
      padding-bottom: var(--space-sm);
      margin: 0;
    }

    .lobby-hint {
      color: var(--text-dim);
      font-size: var(--space-md);
      line-height: 1.45;
    }

    .selection-banner {
      display: grid;
      gap: var(--space-xs);
      padding: clamp(var(--space-sm), 2vw, var(--space-md));
      border-radius: var(--radius-lg);
      border: 1px solid var(--border);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--surface) 84%, var(--bg)), color-mix(in srgb, var(--surface-card-1) 84%, #03060b)),
        radial-gradient(circle at 16% 16%, color-mix(in srgb, var(--gold) 12%, transparent), transparent 30%);
      box-shadow:
        0 1.125rem 2.125rem rgba(0, 0, 0, 0.18),
        inset 0 1px 0 rgba(255, 255, 255, 0.06);
      font-size: var(--space-md);
    }

    .selection-banner.ready {
      border-color: color-mix(in srgb, var(--gold) 16%, transparent);
      animation: state-ready-pulse 2.2s ease-in-out infinite;
    }

    .selection-banner.missing {
      border-color: color-mix(in srgb, var(--danger) 16%, transparent);
      animation: state-danger-shiver 0.4s ease-in-out 2;
    }

    .selection-banner b {
      color: var(--gold);
      font-size: 0.76em;
      letter-spacing: 0.14em;
    }

    .selection-banner span {
      color: var(--text-muted);
      font-size: 0.94em;
      line-height: 1.45;
    }

    .save-list {
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
      max-height: 50vh;
      overflow-y: auto;
    }

    .vessel-slot {
      width: 100%;
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      background:
        linear-gradient(135deg, color-mix(in srgb, var(--surface) 84%, var(--bg)), color-mix(in srgb, var(--surface-card-1) 84%, #03060b)),
        radial-gradient(circle at 84% 16%, color-mix(in srgb, var(--brand-primary) 12%, transparent), transparent 30%);
      color: var(--text-muted);
      text-align: left;
      padding: clamp(var(--space-sm), 2vw, var(--space-md));
      display: grid;
      grid-template-columns: auto 1fr;
      gap: var(--space-sm);
      align-items: start;
      cursor: pointer;
      position: relative;
      overflow: clip;
      transform-style: preserve-3d;
      font-size: var(--space-md);
      box-shadow:
        0 1.25rem 2.25rem rgba(0, 0, 0, 0.22),
        0 0 0 1px rgba(255, 255, 255, 0.03),
        inset 0 1px 0 rgba(255, 255, 255, 0.06);
      transition:
        border-color 0.18s ease,
        transform 0.24s ease,
        box-shadow 0.24s ease;
    }

    .vessel-slot.loading-skeleton {
      cursor: progress;
      pointer-events: none;
      color: var(--text-dim);
      border-color: color-mix(in srgb, var(--border) 86%, #61708a);
      background:
        linear-gradient(135deg, color-mix(in srgb, var(--surface-card-2) 94%, var(--bg)), color-mix(in srgb, var(--surface-card-3) 90%, #03060b)),
        radial-gradient(circle at 84% 16%, color-mix(in srgb, var(--brand-primary) 7%, transparent), transparent 30%);
      box-shadow: var(--shadow-elev-2);
    }

    .vessel-slot:hover,
    .vessel-slot:focus-visible,
    .vessel-slot.active {
      border-color: color-mix(in srgb, var(--brand-primary) 48%, transparent);
      transform: translateY(-0.1875rem) scale(1.01);
      box-shadow:
        0 1.875rem 2.875rem rgba(0, 0, 0, 0.28),
        0 0 0 1px color-mix(in srgb, var(--brand-primary) 16%, transparent),
        inset 0 1px 0 rgba(255, 255, 255, 0.09);
    }

    .vessel-portrait {
      width: 4.6rem;
      height: 4.6rem;
      border-radius: 1rem;
      object-fit: cover;
      border: 1px solid color-mix(in srgb, var(--brand-primary) 16%, transparent);
      box-shadow:
        0 0.875rem 1.625rem rgba(0, 0, 0, 0.28),
        0 0 0 1px rgba(255, 255, 255, 0.04),
        inset 0 1px 0 rgba(255, 255, 255, 0.08);
    }

    .vessel-main {
      display: grid;
      gap: var(--space-xs);
      min-width: 0;
    }

    .vessel-head {
      display: flex;
      justify-content: space-between;
      gap: var(--space-xs);
      align-items: baseline;
    }

    .vessel-head b {
      color: color-mix(in srgb, var(--brand-primary) 84%, var(--text-prose));
      font-size: 1em;
      letter-spacing: 0.02em;
    }

    .vessel-head small {
      color: var(--text-dim);
      font-size: 0.72em;
      letter-spacing: 0.08em;
    }

    .vessel-bio {
      color: var(--text-muted);
      font-size: 0.94em;
      line-height: 1.45;
    }

    .vessel-meta {
      color: var(--text-dim);
      font-size: 0.82em;
      letter-spacing: 0.04em;
    }

    @media (max-width: 45rem) {
      .vessel-slot {
        grid-template-columns: 1fr;
      }
    }
  </style>
</vessels-view>
